// OptiBay Evidence-Driven Diagnostic System — Types

export const OPTIBAY_STAGES = [
  'symptom',
  'system_family',
  'test_paths',
  'evidence',
  'guidance',
  'verification_tests',
  'interpretation',
  'repair',
  'verify',
  'training',
] as const;

export type OptiBayStage = typeof OPTIBAY_STAGES[number];

export const OPTIBAY_STAGE_LABELS: Record<OptiBayStage, string> = {
  symptom: 'Symptom',
  system_family: 'System Family',
  test_paths: 'Test Paths',
  evidence: 'Evidence',
  guidance: 'Guidance',
  verification_tests: 'Verification',
  interpretation: 'Interpretation',
  repair: 'Repair',
  verify: 'Verify',
  training: 'Training',
};

export type OptiBaySessionStatus = 'active' | 'in_progress' | 'completed' | 'abandoned';

export interface OptiBaySystemFamily {
  id: string;
  key: string;
  label: string;
  category_key: string | null;
  is_diagnostic_root: boolean;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export interface OptiBaySubfamily {
  id: string;
  family_id: string;
  key: string;
  label: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export interface OptiBayTestPath {
  id: string;
  family_id: string;
  subfamily_id: string | null;
  key: string;
  label: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export interface OptiBaySession {
  id: string;
  ticket_id: string;
  shop_id: string;
  technician_id: string | null;
  technician_profile_id: string | null;
  symptom_text: string | null;
  system_family_id: string | null;
  subfamily_id: string | null;
  status: OptiBaySessionStatus;
  current_stage: OptiBayStage;
  started_at: string;
  completed_at: string | null;
  created_at: string;
  technician_symptom_notes: string | null;
  technician_symptom_tags: string[] | null;
}

// --- Phase 2: Test Paths + Evidence ---

export type OptiBayTestPathStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';

export interface OptiBaySessionTestPath {
  id: string;
  session_id: string;
  test_path_id: string;
  sort_order: number;
  status: OptiBayTestPathStatus;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export const EVIDENCE_TYPES = [
  'photo',
  'waveform',
  'scan_data',
  'alignment',
  'meter_reading',
  'numeric',
  'boolean',
  'note',
] as const;

export type OptiBayEvidenceType = typeof EVIDENCE_TYPES[number];

export const EVIDENCE_TYPE_LABELS: Record<OptiBayEvidenceType, string> = {
  photo: 'Photo',
  waveform: 'Waveform',
  scan_data: 'Scan Data',
  alignment: 'Alignment',
  meter_reading: 'Meter Reading',
  numeric: 'Measurement',
  boolean: 'Yes/No Check',
  note: 'Technician Note',
};

export interface OptiBaySessionEvidence {
  id: string;
  session_id: string;
  session_test_path_id: string;
  evidence_type: OptiBayEvidenceType;
  artifact_id: string | null;
  value_numeric: number | null;
  value_boolean: boolean | null;
  value_text: string | null;
  label: string | null;
  unit: string | null;
  created_at: string;
}

// --- Phase 3: Guidance + Verification + Interpretation + Repair ---

export type OptiBayGuidanceType = 'pattern_hint' | 'diagnostic_question' | 'spec_reference' | 'safety_warning';

export interface OptiBaySessionGuidance {
  id: string;
  session_id: string;
  session_test_path_id: string | null;
  guidance_type: OptiBayGuidanceType;
  title: string;
  body: string;
  evidence_gate_met: boolean;
  sort_order: number;
  created_at: string;
}

export interface OptiBaySessionVerificationTest {
  id: string;
  session_id: string;
  session_test_path_id: string | null;
  test_name: string;
  description: string | null;
  expected_result: string | null;
  actual_result: string | null;
  passed: boolean | null;
  performed_at: string | null;
  sort_order: number;
  created_at: string;
}

export type OptiBayConfidence = 'low' | 'medium' | 'high';

export interface OptiBaySessionInterpretation {
  id: string;
  session_id: string;
  session_test_path_id: string | null;
  interpretation_text: string;
  confidence: OptiBayConfidence;
  root_cause_category: string | null;
  created_at: string;
}

export type OptiBayRepairStatus = 'planned' | 'in_progress' | 'completed' | 'cancelled';

export interface OptiBaySessionRepair {
  id: string;
  session_id: string;
  interpretation_id: string | null;
  repair_description: string;
  repair_category: string | null;
  parts_needed: string | null;
  estimated_minutes: number | null;
  status: OptiBayRepairStatus;
  completed_at: string | null;
  created_at: string;
}

// --- Phase 4: Verified Repair + Learning Loop ---

export type OptiBayVerificationResult = 'pending' | 'passed' | 'failed' | 'inconclusive';

export interface OptiBaySessionVerification {
  id: string;
  session_id: string;
  repair_id: string | null;
  verification_method: string;
  result: OptiBayVerificationResult;
  notes: string | null;
  verified_at: string | null;
  created_at: string;
  repair_verified: boolean | null;
  symptom_resolved: boolean | null;
  comeback_flag: boolean;
}

export type OptiBayOutcomeStatus = 'successful' | 'partial' | 'unsuccessful' | 'inconclusive';

export interface OptiBayLearningOutcome {
  id: string;
  session_id: string;
  shop_id: string;
  technician_profile_id: string | null;
  system_family_id: string | null;
  symptom_text: string | null;
  root_cause_category: string | null;
  confidence: OptiBayConfidence;
  evidence_types: string[];
  evidence_count: number;
  test_path_count: number;
  verification_pass_rate: number | null;
  repair_count: number;
  total_duration_minutes: number | null;
  outcome_status: OptiBayOutcomeStatus;
  technician_notes: string | null;
  training_linked: boolean;
  created_at: string;
}
